import {
	ActionBar,
	ContentCard,
	ItemWithLabel,
	MenuListButton,
	rem,
	useCompanyDateTime,
	useToggle,
} from '@innovyze/stylovyze';
import { Delete } from '@mui/icons-material';
import { Link } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { deleteIntegration } from '../integrations/actions/integrations.actions';
import { IntegrationsTypeWithLabel } from '../integrations/arcgis/ArcGisSettings/ItemsWithLabel/IntegrationTypeWithLabel';
import { StatusItemWithLabel } from '../integrations/arcgis/ArcGisSettings/ItemsWithLabel/StatusItemWithLabel';
import { IntegrationStatus, IntegrationType } from '../types/integration.types';
import Alert from '@mui/lab/Alert';
import { addRouteNamespace } from '@Utils/actions';
import { useHistory } from 'react-router-dom';
import DeleteIntegrationModal from '../integrations/arcgis/ArcGisSettings/DeleteIntegrationModal';

interface IntegrationCardProps {
	type: IntegrationType;
	lastImportTimestamp?: string;
	isEnabled?: boolean;
	isValid: boolean;
	id: string;
	name: string;
}

export default function IntegrationCard({
	type,
	lastImportTimestamp,
	isEnabled,
	isValid,
	id,
	name,
}: IntegrationCardProps) {
	const dispatch = useDispatch();
	const { t } = useGlobalization();

	const history = useHistory();
	const [isDeleteModalOpen, toggleDeleteModalOpen] = useToggle(false);

	const { formatDate } = useCompanyDateTime();
	let dateFormatted2 = null;
	if (lastImportTimestamp) {
		dateFormatted2 = formatDate({
			date: lastImportTimestamp,
			zoneOptions: { keepLocalTime: true },
			format: 'date-time',
		});
	}

	const onCardClick = useCallback(() => {
		if (type == IntegrationType.ArcGis)
			history.push(addRouteNamespace('integrations/edit/' + id));
		else if (type == IntegrationType.Vapar)
			history.push(addRouteNamespace('integrations/editVapar/' + id));
		else if (type == IntegrationType.ITPipes)
			history.push(addRouteNamespace('integrations/editITPipes/' + id));
	}, []);

	return (
		<ContentCard
			dataCy="integration-card"
			header={
				<ActionBar>
					<Link
						underline="hover"
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							cursor: 'pointer',
							width: '100%',
							whiteSpace: 'nowrap',
							fontWeight: '700',
						}}
						title={name}
						onClick={onCardClick}
						data-cy="card-integration-title">
						{name}
					</Link>
					<MenuListButton
						buttonCy="threeDotMenu"
						menuItems={[
							{
								cy: 'deleteOption',
								text: 'Delete',
								icon: <Delete />,
								onClick: toggleDeleteModalOpen,
							},
						]}
					/>
				</ActionBar>
			}
			footer={
				isValid || (
					<Alert
						variant="filled"
						style={{ width: '100%' }}
						severity="warning">
						{t('Authentication Required')}
					</Alert>
				)
			}
			applyHeaderDivider>
			<DeleteIntegrationModal
				open={isDeleteModalOpen}
				onCancel={toggleDeleteModalOpen}
				onDelete={() => dispatch(deleteIntegration(id))}
			/>
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gridGap: rem(4),
				}}
				onClick={onCardClick}
				aria-hidden="true">
				<IntegrationsTypeWithLabel integrationType={type} />
				<ItemWithLabel
					label={
						type == IntegrationType.Vapar
							? t('Last Used Time')
							: t('Last Updated')
					}
					value={dateFormatted2 ?? '--'}
				/>
				<StatusItemWithLabel
					status={
						isEnabled
							? IntegrationStatus.Enabled
							: IntegrationStatus.Disabled
					}
				/>
			</div>
		</ContentCard>
	);
}
